import {useState} from 'react'
import {useDroppable} from '@dnd-kit/core'
import {SortableContext, useSortable, rectSortingStrategy} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {GripVertical, Trash2} from 'lucide-react'
import {useI18n} from '@/i18n'
import {TooltipProvider} from '@/components/ui/tooltip'
import {Button} from '@/components/ui/button'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {cn} from '@/lib/utils'
import {FieldCard} from './FieldCard'
import type {LayoutSection, DesignerAction, DesignerSelection, Palette} from '@/types/form-layout'

// ============================================================================
// Compute colSpan overflow per field row
// ============================================================================

function computeOverflowFlags(section: LayoutSection): boolean[] {
    const flags = new Array<boolean>(section.fields.length).fill(false)
    let rowSum = 0
    let rowStart = 0

    for (let i = 0; i < section.fields.length; i++) {
        const colSpan = section.fields[i].colSpan ?? 6
        rowSum += colSpan
        if (rowSum > 12) {
            // Mark all fields in this overflow row
            for (let j = rowStart; j <= i; j++) {
                flags[j] = true
            }
        }
        if (rowSum >= 12) {
            rowSum = 0
            rowStart = i + 1
        }
    }
    return flags
}

// ============================================================================
// Section Empty State
// ============================================================================

function SectionEmptyState() {
    const i18n = useI18n()
    return (
        <div className="flex h-20 items-center justify-center rounded border-2 border-dashed border-muted-foreground/30">
            <p className="text-sm text-muted-foreground">{i18n.formLayouts.sectionEmptyState}</p>
        </div>
    )
}

// ============================================================================
// SectionCard
// ============================================================================

interface SectionCardProps {
    section: LayoutSection
    sectionIndex: number
    dispatch: React.Dispatch<DesignerAction>
    selection: DesignerSelection
    onSelect: (sel: DesignerSelection) => void
    palette: Palette | null
}

export function SectionCard({section, sectionIndex: _sectionIndex, dispatch, selection, onSelect, palette}: SectionCardProps) {
    const i18n = useI18n()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        /* eslint-disable i18next/no-literal-string */
        id: `section-${section.key}`,
        data: {type: 'section', sectionKey: section.key},
        /* eslint-enable i18next/no-literal-string */
    })

    const {setNodeRef: dropRef, isOver} = useDroppable({
        /* eslint-disable i18next/no-literal-string */
        id: `droppable-section-${section.key}`,
        data: {type: 'section-drop', sectionKey: section.key},
        /* eslint-enable i18next/no-literal-string */
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const overflowFlags = computeOverflowFlags(section)

    // eslint-disable-next-line i18next/no-literal-string
    const fieldIds = section.fields.map((_, i) => `field-${section.key}-${i}`)

    const isSelectedSection = selection?.type === 'section' && selection.sectionKey === section.key

    return (
        <TooltipProvider>
            <div
                ref={setNodeRef}
                style={style}
                className={cn(
                    'rounded-lg border bg-card shadow-sm transition-opacity',
                    isDragging && 'opacity-50',
                    isSelectedSection && 'ring-2 ring-primary'
                )}
            >
                {/* Section header */}
                <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
                    {/* Drag handle */}
                    <span
                        {...attributes}
                        {...listeners}
                        className="cursor-grab text-muted-foreground active:cursor-grabbing"
                    >
                        <GripVertical className="h-4 w-4" />
                    </span>

                    {/* Label — clicking opens section properties */}
                    <button
                        data-testid="section-label-btn"
                        type="button"
                        className="flex-1 text-left font-medium text-sm cursor-pointer hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                        onClick={() => onSelect({type: 'section', sectionKey: section.key})}
                    >
                        {section.label || <span className="text-muted-foreground italic">{i18n.formLayouts.unnamedSection}</span>}
                    </button>

                    {/* Columns selector */}
                    <Select
                        value={String(section.columns)}
                        onValueChange={(val) =>
                            dispatch({
                                type: 'UPDATE_SECTION',
                                sectionKey: section.key,
                                patch: {columns: Number(val) as 1 | 2 | 3},
                            })
                        }
                    >
                        <SelectTrigger
                            data-testid="section-columns-select"
                            className="h-7 w-20 text-xs"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="1">{i18n.formLayouts.columns1}</SelectItem>
                            <SelectItem value="2">{i18n.formLayouts.columns2}</SelectItem>
                            <SelectItem value="3">{i18n.formLayouts.columns3}</SelectItem>
                        </SelectContent>
                    </Select>

                    {/* Delete button */}
                    <Button
                        data-testid="section-delete-btn"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                            e.stopPropagation()
                            setDeleteDialogOpen(true)
                        }}
                    >
                        <Trash2 className="h-4 w-4" />
                    </Button>
                </div>

                {/* Field grid */}
                <div
                    ref={dropRef}
                    className={cn(
                        'grid grid-cols-12 gap-2 p-3',
                        isOver && 'bg-primary/5'
                    )}
                >
                    <SortableContext items={fieldIds} strategy={rectSortingStrategy}>
                        {section.fields.length === 0 ? (
                            <div className="col-span-12">
                                <SectionEmptyState />
                            </div>
                        ) : (
                            section.fields.map((field, fieldIndex) => (
                                <FieldCard
                                    key={`field-${section.key}-${fieldIndex}`}
                                    sectionKey={section.key}
                                    fieldIndex={fieldIndex}
                                    field={field}
                                    palette={palette}
                                    dispatch={dispatch}
                                    selection={selection}
                                    onSelect={onSelect}
                                    colSpanExceedsRow={overflowFlags[fieldIndex]}
                                    defaultColSpan={Math.floor(12 / section.columns)}
                                />
                            ))
                        )}
                    </SortableContext>
                </div>
            </div>

            {/* Delete section dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {i18n.formLayouts.deleteSectionTitle({label: section.label})}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {i18n.formLayouts.deleteSectionBody}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{i18n.common.cancel}</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            onClick={() => {
                                dispatch({type: 'REMOVE_SECTION', sectionKey: section.key})
                                setDeleteDialogOpen(false)
                            }}
                        >
                            {i18n.formLayouts.deleteSectionConfirm}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </TooltipProvider>
    )
}
